import './Covers.scss'

import React, { useState, useCallback, useRef } from 'react'
import Slider, { Settings } from 'react-slick'
import classNames from 'classnames'
import { isEmpty } from 'lodash'

import Cover from './Cover'
import LinkWrapper from '../../components/LinkWrapper'

import useInterval from '../../hooks/useInterval'
import useDeviceTypeContext from '../../hooks/useDeviceTypeContext'
import { safeMapLoop } from '../../utils/misc'
import { DeviceType } from '../../constants/enums'
import { CoverItemFragment } from '../../types/generated'

interface Props {
  nowLoading: boolean
  coverItems: CoverItemFragment[]
}

const NEXT_COVER_TIME = 1000 * 10

export default function Covers({ nowLoading, coverItems }: Props) {
  const [selectedItemIndex, selectItemIndex] = useState(0)
  const [isMouseOnCoverItems, setIsMouseOnCoverItems] = useState(false)
  const deviceType = useDeviceTypeContext()
  const sliderRef = useRef<Slider>(null)

  useInterval(() => {
    if (isMouseOnCoverItems) {
      return
    }

    if (deviceType === DeviceType.XS && sliderRef.current) {
      return sliderRef.current.slickNext()
    }

    if (!isEmpty(coverItems) && selectedItemIndex === coverItems.length - 1) {
      return selectItemIndex(0)
    }

    selectItemIndex(previousSelectedItemIndex => previousSelectedItemIndex + 1)
  }, NEXT_COVER_TIME)

  const handleCoverItemsMouseEnter = useCallback(() => setIsMouseOnCoverItems(true), [])

  const handleCoverItemsMouseLeave = useCallback(() => setIsMouseOnCoverItems(false), [])

  const renderContentsItem = useCallback(
    () =>
      safeMapLoop(coverItems, (item: CoverItemFragment, i: number) => (
        <li key={i} onMouseEnter={() => selectItemIndex(i)}>
          <LinkWrapper url={item.url}>
            <div
              style={{
                backgroundImage: `url('${item.thumbnailImage}')`,
              }}
              className={classNames('Covers__item', {
                'Covers__item--selected': i === selectedItemIndex,
              })}
            />
          </LinkWrapper>
        </li>
      )),
    [selectedItemIndex, coverItems]
  )

  const renderContentsBullets = useCallback(
    () =>
      safeMapLoop(coverItems, (_, i) => (
        <li key={i}>
          <div
            className={classNames('Covers__itemBullet', {
              'Covers__itemBullet--selected': i === selectedItemIndex,
            })}
            onClick={() => selectItemIndex(i)}
          />
        </li>
      )),
    [selectedItemIndex, coverItems]
  )

  const selectedItem = coverItems[selectedItemIndex]
  const sliderSettings: Settings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: NEXT_COVER_TIME,
    afterChange: (currentItemIndex: number) => {
      if (currentItemIndex < 0 || selectedItemIndex === coverItems.length - 1) {
        return selectItemIndex(0)
      }

      selectItemIndex(currentItemIndex + 1)
    },
  }

  return (
    <div className="Covers">
      {deviceType === DeviceType.XS ? (
        <Slider {...sliderSettings} ref={sliderRef}>
          {coverItems.map(coverItem => (
            <div key={coverItem!.url} className="Covers__coverWrapper">
              <Cover
                item={coverItem}
                nowLoading={nowLoading}
                backgroundImage={`url('${coverItem.coverImage}')`}
                isSlick
              />
            </div>
          ))}
        </Slider>
      ) : (
        <Cover
          item={selectedItem}
          nowLoading={nowLoading}
          backgroundImage={`url('${selectedItem && selectedItem.coverImage}')`}
        >
          <ul
            className="Covers__items"
            onMouseEnter={handleCoverItemsMouseEnter}
            onMouseLeave={handleCoverItemsMouseLeave}
          >
            {renderContentsItem()}
          </ul>
        </Cover>
      )}
      <ul className="Covers__items__bullets">{renderContentsBullets()}</ul>
    </div>
  )
}
